/**
 * Module 35 (WP-35.1) — Block Jam generation config: schema, typed loader, and
 * the in-bundle default.
 *
 * The single shipped, editable artifact is `games/blockjam/config/generation.json`
 * (the Level Studio writes it; the generator reads it). Because that file lives
 * OUTSIDE the package `rootDir` (`src`), the compiled library cannot import it
 * directly without breaking the `tsc` build, so the parity default is authored
 * here as `DEFAULT_GENERATION_CONFIG` and the JSON is kept byte-for-byte in sync.
 * A drift test (`generation-config.test.ts`) parses the JSON with this loader and
 * asserts it deep-equals `DEFAULT_GENERATION_CONFIG`, so the two can never
 * diverge silently.
 *
 * WP-35.1 is plumbing-only: only `bands` and `colours.palette` are consumed by the
 * generator (parity, no behaviour change). Every other section is validated,
 * committed data that later WPs (difficulty score, shapes/arrows, power-ups…) wire
 * in. Injected via the plugin constructor seam — no globals.
 */
import { z } from 'zod';
import { ECONOMY } from '../constants/economy-registry';
// ─── Schema ───────────────────────────────────────────────────────────────────
export const DIFFICULTY_NAMES = ['Easy', 'Medium', 'Hard', 'Expert'];
const difficultyName = z.enum(DIFFICULTY_NAMES);
/**
 * The three player-facing progression tracks (Module 65). Declared here, beside
 * the gameplay bands, because `moveBudgetSchema` needs them and it is defined
 * long before `trackSchema` is.
 */
export const TRACK_IDS = ['beginner', 'intermediate', 'expert'];
const revealTrackSchema = z.object({
    highlightAtOrBelow: z.number().int().min(0),
    requireShortfall: z.boolean(),
    fromAttempt: z.number().int().min(1),
    maxPerPuzzle: z.number().int().min(0),
    tipEveryPuzzle: z.boolean(),
});
/**
 * The seven progression bands from the Difficulty Framework (§5). These are a
 * CURVE vocabulary, not a gameplay one: each maps onto one of the four
 * `DIFFICULTY_NAMES` that boards, saves, analytics, coin rewards and XP all
 * speak, so a framework name never escapes the generator (WP-35.10).
 */
export const FRAMEWORK_BAND_NAMES = [
    'Beginner',
    'Easy',
    'Easy+',
    'Medium',
    'Medium+',
    'Advanced',
    'Infinite',
];
/** A record keyed by every GAMEPLAY band name (Easy/Medium/Hard/Expert). */
const perBand = (value) => z.object({
    Easy: value,
    Medium: value,
    Hard: value,
    Expert: value,
});
/**
 * A record keyed by every FRAMEWORK band name (Beginner…Infinite) — WP-36.6.
 *
 * Which vocabulary a section uses is a design decision, not an accident:
 *  - Things the PLAYER experiences as a per-level ramp (block count, colour
 *    count, arrow density) are keyed here, because the four gameplay bands are
 *    too coarse to tune with. Gameplay-`Easy` spans levels 1–25, so a single
 *    ramp had to cover a player's first board and their twenty-fifth; splitting
 *    it into Beginner (1–10) and Easy (11–25) is what makes "gentle but varied
 *    early on" expressible at all (WP-36.7).
 *  - Things a BOARD is built as (grid size, solver limits, shape weights, cell
 *    ceiling) stay on `perBand`, because those are the labels that reach saves,
 *    analytics, rewards and XP.
 */
const perFrameworkBand = (value) => z.object({
    Beginner: value,
    Easy: value,
    'Easy+': value,
    Medium: value,
    'Medium+': value,
    Advanced: value,
    Infinite: value,
});
/**
 * A `{min,max}` ramp endpoint pair. Deliberately NOT integer-constrained even
 * where it names a count: the consumer ramps between the two and rounds, so a
 * fractional endpoint is the only way to express a ramp whose whole-number steps
 * do not fall on a band boundary. `refine` keeps the pair ordered.
 */
const rampRange = (label) => z
    .object({
    min: z.number().positive(),
    max: z.number().positive(),
})
    .refine((r) => r.max >= r.min, { message: `${label}: max must be >= min` });
/**
 * How a board of a given GAMEPLAY band is built. Looked up by `bandConfigFor`
 * from the band `curve.bands[].difficulty` names — a band is identified here by
 * `name` alone, and knows nothing about which levels it covers.
 *
 * WP-50.23 removed `levelRange`. It held the LEGACY single-axis ranges (Easy
 * 1–15, Medium 16–30, …) and its only reader was `difficultyForLevel`, which
 * had itself lost its last caller when WP-35.23 deleted the single-axis
 * generator. Level → band has been the seven `curve.bands` since WP-35.10, so
 * the field was a second, silently-disagreeing answer to a question the config
 * already answers elsewhere: it said gameplay-Easy ended at level 15 while the
 * curve said 10. Proven dead by measurement, not inspection — see
 * `config-liveness.test.ts`.
 */
const bandSchema = z.object({
    name: difficultyName,
    gridSize: z.number().int().positive(),
    minSolveMoves: z.number().int().nonnegative(),
    maxAttempts: z.number().int().positive(),
    /**
     * Node cap for the free-slide solver. Values come from the WP-35.6 benchmark
     * and bound a cap-hit worst case to ≲250ms, keeping generation inside its
     * 300ms budget.
     *
     * WP-35.24 removed the sibling fields `numH`, `numV`, `shuffleSteps` and
     * `solverLimit`: they configured the single-axis generator and its solver,
     * both deleted in WP-35.23, and nothing had read them since. Leaving four
     * required-but-dead knobs in a frozen rule book would have been worse than the
     * small schema change.
     */
    freeSolverLimit: z.number().int().positive(),
});
const coloursSchema = z.object({
    palette: z.array(z.string().min(1)).min(1),
    /**
     * How many DISTINCT colours a level uses, as a range per FRAMEWORK band
     * (WP-35.15 shape, re-keyed in WP-36.6) — ramped across the band exactly like
     * `density.blocksPerBand`, so the count climbs 2 → 7 across the ladder instead
     * of stepping four times. A single number per band (the WP-35.1 shape) meant
     * all 25 levels of Easy shipped the same colour count, and the anti-repetition
     * rule had nothing to work with once the count equalled the palette size.
     */
    countPerBand: perFrameworkBand(rampRange('countPerBand')),
});
const shapesSchema = z.object({
    families: z
        .array(z.object({
        id: z.string().min(1),
        weightPerBand: perBand(z.number().nonnegative()),
    }))
        .min(1),
    /**
     * How many blocks of ONE family a level may use (WP-35.14). Weights alone are
     * happy to draw four L-blocks out of eight, which is both repetitive and, at
     * three cells each, expensive to place — level 135 measured 154 attempts and
     * 1.1s before this cap. Relaxed automatically when no other family is unlocked
     * (levels 1–7 have only the 1x2).
     */
    maxSameFamily: z.number().int().positive(),
});
const unlocksSchema = z.object({
    arrowsUnlockLevel: z.number().int().positive(),
    shapes: z.record(z.string(), z.number().int().positive()),
    powerups: z.record(z.string(), z.number().int().positive()),
    /**
     * Module 63 (WP-63.D1) — the first level that may carry a powered gate. The
     * unlock is a PROMISE (§3 rule 1): the generator forces ≥ 1 powered gate on
     * this level, and never places one before it, which is what keeps L1–24
     * byte-identical (module decision 2).
     */
    poweredGatesUnlockLevel: z.number().int().positive(),
});
const arrowsSchema = z.object({
    /**
     * What fraction of a level's blocks carry an axis lock, as a range per
     * FRAMEWORK band, ramped across the band (WP-36.6). It was one flat number per
     * GAMEPLAY band, which is why the arrow count sat at exactly 1 from level 5 to
     * level 70: `fraction × blocks` only crosses 2 once both sides have climbed,
     * and gameplay-Easy held one fraction across 25 levels.
     */
    constraintFractionPerBand: perFrameworkBand(z
        .object({
        min: z.number().min(0).max(1),
        max: z.number().min(0).max(1),
    })
        .refine((r) => r.max >= r.min, {
        message: 'constraintFractionPerBand: max must be >= min',
    })),
    /**
     * A per-band FLOOR on the arrow count (WP-36.6), applied after the fraction.
     * The fraction alone cannot guarantee a minimum, because it is multiplied by a
     * block count that dips on recovery levels — so without this a late-game
     * recovery level could drop back to zero arrows, which reads as the mechanic
     * being taken away rather than as a breather.
     */
    minArrowsPerBand: perFrameworkBand(z.number().int().nonnegative()),
});
const curveBandSchema = z.object({
    name: z.enum(FRAMEWORK_BAND_NAMES),
    levelRange: z.object({
        min: z.number().int().positive(),
        /** `null` = open-ended; the last band catches every remaining level. */
        max: z.number().int().positive().nullable(),
    }),
    /** Difficulty-score window this band aims for (WP-35.9's 0–70+ scale). */
    targetScore: z.object({
        min: z.number().nonnegative(),
        max: z.number().nonnegative(),
    }),
    /** The gameplay band this maps to — the only label that leaves the generator. */
    difficulty: difficultyName,
});
const curveSchema = z.object({
    bands: z.array(curveBandSchema).min(1),
    /** How far off target a board may land and still be accepted. */
    tolerance: z.number().positive(),
    /**
     * How many accepted boards to score and choose between. Each costs a proxy
     * score (a small-budget solve), so this trades generation time for how tightly
     * levels hug the curve.
     */
    candidates: z.number().int().positive(),
    /**
     * Hard stop on how many candidate boards may be BUILT while chasing the target
     * (WP-35.14). `candidates` alone does not bound the work: on a crowded late
     * board most attempts are rejected as unsolvable at ~7ms each, so asking for 5
     * scored candidates cost 62 attempts and 420ms on level 114 — past the module's
     * 300ms budget. Deterministic (an attempt count, never a clock), so boards stay
     * identical across machines.
     */
    candidateAttemptBudget: z.number().int().positive(),
    /**
     * Milestone levels aim at the TOP of their band's window (a memorable peak,
     * Framework §18) — this is how far up, 0..1.
     */
    milestoneLift: z.number().min(0).max(1),
});
const difficultyScoreWeightsSchema = z.object({
    moves: z.number(),
    branchingFactor: z.number(),
    decisionPoints: z.number(),
    occupancy: z.number(),
    shapeVariety: z.number(),
    colourCount: z.number(),
    searchDepth: z.number(),
});
const densitySchema = z.object({
    /** Block count for level 1 — also the floor a recovery dip cannot go under. */
    startBlocks: z.number().int().positive(),
    /**
     * Minimum number of blocks that CANNOT reach their gate on their own (WP-35.11):
     * something else has to move first. A board with zero dependencies is a row of
     * blocks each sliding straight out, which is what made early levels feel empty.
     */
    startDependencies: z.number().int().nonnegative(),
    /** How many blocks a recovery level sheds (Framework §17). */
    recoveryBlockDrop: z.number().int().nonnegative(),
    /**
     * Ceiling on how much of the grid a level may FILL, 0..1 (WP-35.14). Block
     * count is only a proxy for fullness while every block is a 1x2; once the shape
     * schedule started delivering 1x4 / 2x3 / L on time, the same count carried far
     * more cells. `blocksPerBand` is the ambition, this is the hard ceiling — a
     * level that would exceed it simply gets fewer blocks.
     *
     * Stays on the GAMEPLAY bands: it is a property of the grid the board is built
     * on (which is itself per gameplay band), not of the progression ramp.
     */
    maxCellFraction: perBand(z.number().min(0.1).max(1)),
    /**
     * Block count RANGE per FRAMEWORK band (WP-35.11 shape, re-keyed in WP-36.6).
     * A level ramps from `min` at the first level of the band to `max` at its last
     * — a single number per band (the WP-35.8 shape) made every level of a band
     * the same density.
     */
    blocksPerBand: perFrameworkBand(rampRange('blocksPerBand')),
});
const recoverySchema = z.object({
    everyNLevels: z.number().int().positive(),
    dropBands: z.number().int().nonnegative(),
});
const milestonesSchema = z.object({
    levels: z.array(z.number().int().positive()),
});
const antiRepetitionSchema = z.object({
    /**
     * How many previous levels a board's layout must differ from (WP-35.11). Only
     * enforced when the caller supplies that history — `FreeLevelGenerator` keeps
     * it; the pure `generateFreeLevel` has no memory of its own.
     */
    noRepeatWindow: z.number().int().nonnegative(),
    /** Max levels in a row that may use the same multiset of shapes. */
    maxConsecutiveSameShape: z.number().int().positive(),
    /** Max levels in a row that may use the same set of colours. */
    maxConsecutiveSameColourSet: z.number().int().positive(),
});
const powerupSchema = z.object({
    id: z.string().min(1),
    cost: z.object({
        coins: z.number().int().nonnegative(),
        gems: z.number().int().nonnegative(),
    }),
    perLevelCap: z.number().int().positive(),
    /**
     * Module 63 (WP-63.E5) — uses that cost nothing, ONCE EVER per player (not
     * per level, not per load): persisted in the game save as a consumed count.
     * Absent = 0. Surge ships with 3, so the first three times a player meets a
     * shut gate the tool is free to try.
     */
    freeUses: z.number().int().nonnegative().optional(),
    /**
     * Module 92 — switched OFF (user decision 2026-10-05: switch off, not delete).
     * A retired power-up keeps its cost, cap and unlock entry so turning it back
     * on is this flag (plus its server price row — migration 0019 removes it),
     * but it never reaches a player: no button, no unlock
     * card, no lesson, no price. Every reader asks `isPowerupRetired`
     * (`powerups/powerup-registry.ts`); its own effect and tests still run.
     * Absent = live.
     */
    retired: z.boolean().optional(),
});
/**
 * WP-50.23 removed `perBand`. It was a `{ Easy: 10, Medium: 10, Hard: 10,
 * Expert: 10 }` block with **no reader anywhere** — the payout has come from
 * `scoreTiers` → a remote-config key since WP-35.22, and the four amounts live
 * on the server allowlist regardless. A plausible-looking coin table beside a
 * real reward system is a trap for whoever next tunes the economy, which in
 * Module 50 is the very next work package.
 */
const rewardsSchema = z.object({
    /**
     * When true the coin tier comes from the level's difficulty score rather than
     * its band (WP-35.22), so a level that came out harder than its rung pays more.
     *
     * ⚠ The tier **replaces** the band; it is not the maximum of the two. A Hard
     * board whose score lands under `scoreTiers[Medium].minScore` pays the *Easy*
     * amount. See `reward-tier.ts` and §4 of `BlockJam_Generation_Rules.md`.
     */
    scaleWithScore: z.boolean(),
    /**
     * Ascending score thresholds → reward tier. The tier picks which of the four
     * server-allowed amounts is credited; see `reward-tier.ts` for why the amount
     * itself is never computed from the score.
     */
    scoreTiers: z
        .array(z.object({
        tier: z.enum(DIFFICULTY_NAMES),
        minScore: z.number().nonnegative(),
    }))
        .min(1),
});
/**
 * Module 50 (WP-50.28a) — the move budget.
 *
 * ## Why the formula is not `ceil(par × multiplier)`
 *
 * It was going to be. Par is the optimal move count, and the only place it
 * exists at run time is inside the proxy score the generator already computes
 * for the board it chose (`PROXY_NODE_BUDGET` = 4000 nodes). Measured over
 * levels 1–50 before any of this was built
 * (`planning/50-prelaunch-fixes/harness/par-source.mjs`):
 *
 *   - the proxy solves exactly on **32%** of levels;
 *   - it under-reads par on **34 of 50**, worst ratio **0.385** (L12: 5 vs 13);
 *   - `ceil(proxyPar × 2.2)` is therefore **below the true par on L12 and L26** —
 *     levels that cannot be finished inside their own budget. Restart regenerates
 *     the same seeded board with the same budget, so that is a permanent
 *     progression block, which is the one failure this mechanism must not have.
 *
 * An exact solve at level entry is not the answer either: measured over L1–60 it
 * costs a 100ms median but 1.0s at p90 and 2.5s worst at the budget that solves
 * 98% of boards, on a click path that already pays up to 300ms for generation.
 *
 * So the budget is built from two things the generator hands over for free, and
 * the safety comes from the second:
 *
 *   `max(ceil(max(proxyPar, blocks) × multiplier), clearingDepth)`
 *
 *   - **`blocks` is a provable lower bound on par** — every block must leave the
 *     board and each move moves one block, so par ≥ blocks. It is what turns the
 *     proxy's under-read from a wall into a floor, and it alone fixes L12/L26.
 *   - **`clearingDepth` is a provable upper bound on par** — it is the length of
 *     a clearing sequence the generator actually found and verified, so a budget
 *     at least that large can always be finished. This is what makes "never
 *     unwinnable" a guarantee rather than a sample of 50.
 *
 * Delivered over L1–50: median budget **2.7× par**, never below par. On one level
 * (L9, witness 80 against par 9) the witness dominates and there is effectively
 * no wall — a loose budget on one board is a far better failure than a blocked
 * one, and WP-50.28b can tighten it by improving the estimate rather than by
 * removing the guarantee.
 *
 * ## WP-50.28b — the witness was not one level, it was two thirds of them
 *
 * The balance pass measured the *delivered* budget rather than the guarantee,
 * and the sentence above turned out to understate the problem by a factor of 34:
 * `clearingDepth` was the binding term on **34 of 50 levels**, up to **8.5× par**
 * on the shipped curve and **27.6×** on the retuned one. The multiplier was a
 * dead knob on those levels — moving it changed nothing at all — so "settle the
 * multipliers" could not have been done by settling the multipliers.
 *
 * Two config-shaped changes, exactly as the note above hoped: the estimate got a
 * better predictor (`parEstimate`, using the dependency count the generator
 * already has, median **0.98× par** against `blocks`'s 0.64×), and the witness
 * got a ceiling (`witnessCap`) so it can raise a budget but no longer replace
 * it. Delivered over the retuned L1–50: **median 2.6× par, min 1.85×, max 4.1×,
 * never below par.** The multipliers themselves barely moved, which is the
 * honest result: they were never what was broken.
 */
const moveBudgetSchema = z.object({
    /**
     * Per gameplay band — and, since WP-50.28b, roughly the budget-to-par ratio
     * the band actually delivers, because `parEstimate` centres on par.
     *
     * ## WP-50.28c — why these dropped from 3.0/2.2/1.9/1.8
     *
     * 28b tuned the **ratio** and declared victory at a median 2.6× par with every
     * level inside range. A player then reported level 6 offering **41 moves** for
     * a board that takes 11, and both statements were true: 3.73× is inside the
     * range, and it is also **thirty spare moves**. A wall thirty moves away is not
     * a wall, so the out-of-moves offer — the placement the whole of Increment H is
     * built to earn from — could not fire on the early ladder.
     *
     * **The unit was wrong.** A ratio is not what a player experiences; the count
     * of mistakes they may make is, and that is `budget − par`. Two boards at the
     * same ratio hand out completely different amounts of rope, and the early
     * levels — smallest par, largest multiplier — handed out the most. Measured
     * before the change over L1–50: slack min 9, **median 15**, max 30.
     *
     * Delivered after (`harness/budget-terms.mjs`): slack min 2, **median 4**,
     * max 8; ratio median 1.5×; **0 of 50 unwinnable**. Level 6 is 41 → 17.
     *
     * **This is the tightest of four measured candidates, chosen by the user with
     * its risk stated.** The safety margin is now the thinnest it has been: see
     * `parEstimate` below for the under-read that would have to happen for a level
     * to become unwinnable, and `witnessCap` for the term that stopped protecting
     * against it.
     */
    multiplierPerBand: z.record(z.enum(DIFFICULTY_NAMES), z.number().positive()),
    /**
     * Module 65 (WP-65.A3) — the multiplier by TRACK, which wins over the band
     * when the caller knows which track it is on.
     *
     * The band multipliers above are all 1.1 and were tuned as one number for one
     * ladder, where every player met every level. Tracks break that: a player who
     * taps Beginner and a player who taps Expert are not the same player, and
     * handing them the same rope means the beginner hits the ad-gated wall in
     * their first minutes while the expert barely feels it.
     *
     * **Expert was 1.1 until Module 83.** `parEstimate`'s worst under-read was
     * measured at 1.30x par on 3–8 block boards, and 1.1 was "the floor that
     * measurement supports". On Expert's 9–11 block boards the under-read is up
     * to 2.5x (`proxyPar` 7 against a true par of 16–20), and 1.1 shipped 19 of
     * Expert's first 40 budgets below the shortest known solution — three proven
     * impossible by exhaustive search. Expert went to 1.2, then settled at 1.15
     * (user decisions 2026-10-02) and, more importantly, no multiplier carries the winnability
     * guarantee any more: the `solution` floor below does, on every track.
     *
     * **Module 146 — Beginner 1.05, Intermediate 1.1** (user, 2026-10-23). The
     * route a drag beats by about a quarter already gives a player rope, and the
     * user finished every Beginner/Intermediate puzzle 4–10 moves early. The
     * multipliers no longer rank the tracks on the same board (1.05 < 1.15):
     * Beginner's ease is its smaller boards. Slack over the route over puzzles
     * 1–100: Beginner 4.5 → 0.9, Intermediate 1.8 → 0.7, none below the route.
     */
    multiplierPerTrack: z.record(z.enum(TRACK_IDS), z.number().positive()),
    /**
     * Module 69 — extra rope over a track's OPENING puzzles, decaying to the
     * track's own multiplier.
     *
     * `multiplierPerTrack` answers "how much rope does this kind of player get",
     * and it answers it with one number for the whole track. That is the right
     * shape for a ladder a player walks up, and the wrong shape for a track they
     * can START on: Expert puzzle 1 is effective level 61 — 11 blocks, 11
     * dependencies, the hardest boards the generator makes — and it is the FIRST
     * board that player has ever seen. It was delivering a budget of 23 against a
     * predicted par of 18.7, and the user cleared it in exactly 23 of 23: a clean
     * run with zero margin, on the first puzzle of the track.
     *
     * Two things stack behind that, and neither is visible in the multiplier:
     *
     *  1. `parEstimate` is **out of sample** on Expert. Its measured "median 0.98×
     *     par" comes from the retuned L1–50 — 3–8 block boards. An 11-block board
     *     with 11 dependencies is twice that size, and depth-limited BFS could not
     *     close one inside 1.2M nodes, so the true par there is not known. The
     *     tightest budget in the game sits on the least-verified estimate.
     *  2. **No power-up is unlocked.** WP-65.F1 keys the unlocks to the puzzle
     *     ORDINAL (10/20/30/40/50) on every track, deliberately, so each track
     *     teaches them in order — which means Expert's first nine puzzles are the
     *     hardest boards in the game played with no tools at all. The only out is
     *     the rewarded ad, on puzzle 1 of a track.
     *
     * So the relief is aimed at the opening rather than at the track: `multiplier`
     * is what puzzle 1 gets, and it decays linearly to the track's own value over
     * `puzzles` puzzles. It can only ever RAISE — `max()` against the track
     * multiplier — so Beginner's 1.4, already above the grace peak, is untouched,
     * and this field can never become a way to tighten a track's opening.
     *
     * Set `puzzles: 0` to turn the whole mechanism off.
     *
     * **Module 146 — the peak is per track.** The user retuned Beginner and
     * Intermediate to 1.05 / 1.1 after finishing every puzzle 4–10 moves early, and
     * their openings to a 1.2 peak (Beginner then 1.1, after the user's web
     * play-test); Expert kept the 1.45 that Module 69 measured its way into. `multiplierPerTrack` names a track's own peak; a track it
     * leaves out uses `multiplier`. The `max()` above still holds per track: a
     * peak below a track's multiplier is a no-op, never a tightening.
     */
    entryGrace: z.object({
        /** How many opening puzzles the grace covers; 0 disables it. */
        puzzles: z.number().int().min(0),
        /** The multiplier puzzle 1 gets, before the decay starts. */
        multiplier: z.number().positive(),
        /** Module 146 — a track's own puzzle-1 multiplier; a missing track uses `multiplier`. */
        multiplierPerTrack: z.partialRecord(z.enum(TRACK_IDS), z.number().positive()).prefault({}),
    }),
    /**
     * Module 146 — the Daily's multiplier. The Daily passes `track: 'intermediate'`
     * (Module 67: its 25–55 band is Intermediate's territory), so until this field
     * it rode Intermediate's multiplier. When Intermediate tightened 1.2 → 1.1 the
     * user kept the Daily at 1.2: it is one board a day with its own retry economy
     * (Module 118), and it moves on its own decision, not as a side effect.
     * `.default` is safe here — a number, not an object (zod 4's `.default` trap).
     */
    dailyMultiplier: z.number().positive().default(1.2),
    /**
     * How the budget estimates par from what the generator already knows for free,
     * before any search runs. `blocks + 0.7 × dependencies` lands at a median
     * **0.98× par** over the retuned L1–50 (range 0.78–1.40) where `blocks` alone
     * lands at 0.64× (range 0.50–0.88) — and it is the reason the multipliers
     * above mean something.
     *
     * **WP-50.28c re-measured the tail, and it is the number that now matters.**
     * With par solved on 44 of the first 50 levels (4M nodes — 28b's scans stopped
     * at 100k and knew par on far fewer), the worst UNDER-read is **1.30×**, at
     * level 50. The tightest delivered budget is 1.05× par, also level 50. Those
     * two numbers crossing is why the winnability guarantee moved onto the ad
     * grant rather than onto the budget alone.
     *
     * **`perBlock` is floored at 1 on purpose.** It keeps `predicted >= blocks`,
     * which is what preserves 28a's provable lower bound on par: every block must
     * leave the board, and one move moves one block.
     */
    parEstimate: z.object({
        perBlock: z.number().min(1),
        perDependency: z.number().min(0),
    }),
    /**
     * How far above the estimate the verified witness may push the budget. The
     * witness (`clearingDepth`) is a *DFS* result — the first clearing sequence the
     * search stumbles on, measured up to 27.6× par — so used as an uncapped floor
     * it does not make the budget safe, it makes it absent. At 1 the witness is
     * ignored entirely and the guarantee rests wholly on `parEstimate`; above 1 it
     * buys back margin at the cost of a looser wall.
     *
     * **WP-50.28c took it 1.3 → 1.05**, which is nearly all the way to ignoring it.
     * That is the term that carried most of the tightening — it now binds the
     * result on 31 of 50 levels, where the multipliers bind on 8 — and it is also
     * where most of the safety margin went. What replaces it is the ad grant: see
     * `adGrants` below. Restart does NOT replace it — a seeded board returns the
     * same board and the same budget, so only the ad grant moves the wall. Since
     * the grant was tapered to `[2, 1]`, that buffer shrank from +15 to +3, and
     * `bj-move-budget.test.ts`'s `WP-50.28c` case (which sums the taper) now clears
     * `blockCount × 2` by only +1 move at its tightest. Lowering the grant further,
     * or this cap, goes red there — and the budget, not the grant, is what loosens.
     */
    witnessCap: z.number().min(1),
    /**
     * Module 83 (WP-83.1/83.2) — the stored short solution and the budget floor
     * it sets. `maxNodes` is `shortSolve`'s budget at generation (60 000: 120/120
     * boards over puzzles 1–40 of each track solved, 0.4 s average, 5.7 s worst;
     * 15 000 left 12 of them 1–5 moves longer). The floor is
     * `max(length + extra, ceil(length × multiplier))` — see `moveBudgetFor` — and
     * ships with NO margin (`extra: 0`, `multiplier: 1`): the route is straight
     * full slides, while a drag may stop part-way and turn corners, so the route
     * already overstates what a player needs (Daily 2026-10-02: route 16, drag
     * par 10). Only a budget that sat BELOW a known route moves, and only up to it.
     * `.prefault` so a config written before Module 83 parses (zod 4's
     * `.default` would hand back the literal unparsed).
     */
    solution: z
        .object({
        maxNodes: z.number().int().positive().default(60000),
        extra: z.number().int().min(0).default(0),
        multiplier: z.number().min(1).default(1),
    })
        .prefault({}),
    /**
     * Module 83 (WP-83.4) — the Reveal page's coin price (Increment B spends it;
     * migration 0017 allowlists both amounts). `coins` beside a rewarded ad on an
     * ad host, `coinsWithoutAds` alone where the host has none (PuzzleMania Web).
     */
    reveal: z
        .object({
        coins: z.number().int().nonnegative().default(2000),
        coinsWithoutAds: z.number().int().nonnegative().default(2500),
        /**
         * Module 83 (WP-83.7) — the low-moves highlight, per track: Module 81's
         * D3 table carried over from the Hint. Beginner lights at ≤2 moves left,
         * only on a real shortfall, from the 2nd attempt, once per puzzle;
         * Intermediate ≤3, shortfall, from the 1st attempt, twice; Expert ≤5, no
         * shortfall needed, every attempt. `tipEveryPuzzle`: Expert's first
         * highlight on each puzzle raises the tip; the others only once ever.
         */
        tracks: z
            .object({
            beginner: revealTrackSchema.prefault({
                highlightAtOrBelow: 2,
                requireShortfall: true,
                fromAttempt: 2,
                maxPerPuzzle: 1,
                tipEveryPuzzle: false,
            }),
            intermediate: revealTrackSchema.prefault({
                highlightAtOrBelow: 3,
                requireShortfall: true,
                fromAttempt: 1,
                maxPerPuzzle: 2,
                tipEveryPuzzle: false,
            }),
            expert: revealTrackSchema.prefault({
                highlightAtOrBelow: 5,
                requireShortfall: false,
                fromAttempt: 1,
                maxPerPuzzle: 0,
                tipEveryPuzzle: true,
            }),
        })
            .prefault({}),
        /** How long the "Stuck?" tip stays up, unless tapped away first. */
        tipMs: z.number().int().min(0).default(4000),
    })
        .prefault({}),
    /**
     * A fallback board carries no verified solution and no proxy score, so neither
     * term of the formula exists. `blocks × blocksMultiplier`, floored — and
     * deliberately loose: a board we could not verify must never be the one that
     * walls a player.
     */
    fallback: z.object({
        floor: z.number().int().positive(),
        blocksMultiplier: z.number().positive(),
    }),
    /**
     * Moves granted per rewarded ad at the wall, as a TAPERING schedule: the i-th
     * ad taken on a level grants `adGrants[i]` moves, and `maxAdsPerLevel` caps how
     * many a player may take before the offer becomes restart-only. Keep the
     * array's length equal to `maxAdsPerLevel` — an entry past the cap is never
     * read, and a cap past the array's end reads as a 0 grant.
     *
     * History: **WP-50.28b** raised a flat grant 2 → 5 and it stood at `5 × 3`,
     * ~60% of a typical budget across three ads — a wide rescue buffer. **A later
     * retune tapered it to `[2, 1]` (max 2)**: a first ad worth +2, a second worth
     * +1, no third. The ad is now a small nudge rather than a wide buffer — but it
     * is STILL the finishability mechanism, because on a tight board Restart does
     * not help (levels are seeded, so it returns the same board and the same
     * budget; see the `witnessCap` note above). What preserves the guarantee is
     * that `budget + Σ adGrants ≥ blockCount × 2` still holds: re-verified across
     * ALL 120 delivered levels after the taper, with a worst margin of +1 move
     * (level 10). `bj-move-budget.test.ts`'s `WP-50.28c` case sums this schedule.
     * The margin is now thin, so cutting the grant further — or tightening the
     * multipliers — goes red, and then the budget, not the grant, is what loosens.
     *
     * **Module 118 (2026-10-14): back up to `[3, 3]`** (user decision): a +1
     * rescue that still fails feels like being cheated, and every rescue should
     * usually finish. Ad and coin rescues now share one cap of two per attempt
     * (`RESCUES_PER_ATTEMPT`); the coin rescue grants +5. Raising the grant only
     * widens the finishability margin above.
     */
    adGrants: z.array(z.number().int().nonnegative()).min(1),
    maxAdsPerLevel: z.number().int().nonnegative(),
    /** Remaining moves at or below which the HUD's Moves pill warns. */
    warnAtRemaining: z.number().int().nonnegative(),
    /**
     * Module 81 (WP-81.4/81.6) — the in-game 💡 Hint button: one rewarded ad,
     * one demonstrated move, NO moves granted (the Module 80 popup hint granted
     * +2 per ad, uncapped and outside `maxAdsPerLevel`, so it undercut the
     * moves ad). Per track:
     *
     * - `cap` — hints per puzzle (counted across restarts, persisted).
     * - the low-moves HIGHLIGHT fires when moves left ≤ `highlightAtOrBelow`
     *   (and > 0), and — with `requireShortfall` — only when the moves left
     *   cannot clear the board (`movesLeft < blocksLeft`: every block needs at
     *   least one move); from attempt `fromAttempt` (restarts + 1); at most once
     *   per attempt and `maxPerPuzzle` per puzzle (`0` = no per-puzzle limit).
     * - the "Stuck? Watch an ad for a hint" TIP rides the first highlight a
     *   player ever sees (once ever, any track) and, with `tipEveryPuzzle`, the
     *   first highlight of each puzzle; it stays up `tipMs`.
     */
});
/**
 * Module 63 (WP-63.D1) — powered gates: how many, and how far their plates may
 * sit from the gate's wall.
 *
 * `countPerBand` is keyed by FRAMEWORK band like the arrow fraction, because
 * it is a per-level ramp the player experiences; `traceLengthCapPerBand` is
 * keyed by GAMEPLAY band, because it is a constraint a board is *built as*
 * (§5 of the rules). Both follow the vocabulary rule rather than inventing a
 * third one.
 */
const poweredSchema = z.object({
    /**
     * Powered gates per level, as a `{min,max}` ramp across the framework band,
     * interpolated then ROUNDED like `arrows.constraintFractionPerBand` — so a
     * fractional endpoint is how a ramp steps from 1 to 2 mid-band. A count,
     * not a fraction of gates: a board has one gate per colour and few colours,
     * so a fraction would round to the same one or two numbers anyway while
     * hiding which.
     */
    countPerBand: perFrameworkBand(z
        .object({
        min: z.number().min(0),
        max: z.number().min(0),
    })
        .refine((r) => r.max >= r.min, { message: 'powered.countPerBand: max must be >= min' })),
    /**
     * How many gates must stay UNPOWERED whatever the ramp says — the decision-7
     * heuristic: a board with every gate shut has no first move that frees
     * anything, and rejects itself at the solver after paying for the search.
     * A bias for the accept rate, never a solvability rule.
     */
    keepUnpoweredGates: z.number().int().nonnegative(),
    /**
     * The plate's maximum distance from its gate's wall, in cells (0 = the cell
     * touching the wall), per gameplay band; `null` = unrestricted. A long trace
     * looks striking; a long trace on an Easy board is unteachable (module
     * CLAUDE.md §3, generator constraints).
     */
    traceLengthCapPerBand: perBand(z.number().int().nonnegative().nullable()),
});
/**
 * Module 65 (WP-65.A1) — the three difficulty TRACKS.
 *
 * A track is the player-facing unit of progression: they choose one on the
 * Blockway home screen and press Next inside it forever. It is described here by
 * a slice of the SAME `curve.bands` ladder the numbered game used, because the
 * whole point of the module is that the ladder was fine — only its exposure to
 * the player was wrong.
 *
 * ## Two numbers, and why
 *
 * `track-map.ts` turns `(track, ordinal)` into `{ effectiveLevel, variant }`:
 *
 *  - the **ordinal** `k` is what the player sees (`Puzzle 12`), counts up
 *    forever, and drives the board variant, the background, the music and the
 *    every-N rhythms;
 *  - the **effective level** `f(k)` walks `firstLevel → lastLevel` over
 *    `rampLength` puzzles and then HOLDS, and drives difficulty, unlocks, par
 *    and the reward tier.
 *
 * Holding is what makes a track endless without making it endlessly harder. The
 * new boards come from the variant, not from a rising score — see
 * `free-generator.ts`'s `options.seed`.
 *
 * NOTE the name collision, which is deliberate and must not be "fixed":
 * `Beginner` is BOTH a track id and a `curve.bands` name, and they are different
 * things. The Beginner *band* is the first seven puzzles of the Beginner
 * *track*. Unifying them would silently re-key every ramp in this file.
 */
const trackSchema = z
    .object({
    id: z.enum(TRACK_IDS),
    /** The `curve.bands` level this track's puzzle 1 is generated at. */
    firstLevel: z.number().int().positive(),
    /** The level it ramps to and then holds at, forever. */
    lastLevel: z.number().int().positive(),
    /**
     * How many puzzles the ramp takes. `rampLength === lastLevel - firstLevel + 1`
     * makes `f(k) = firstLevel + k - 1` exactly; anything else spreads or
     * compresses the same slice, which is what lets the ramp be retuned by
     * WP-65.A2's measurements without moving the band boundaries.
     */
    rampLength: z.number().int().positive(),
    /**
     * What the endless phase does with the effective level (WP-65.A2).
     *
     * `pin` freezes it at `lastLevel`. That is the obvious reading of "ramps,
     * then holds" and it MEASURED BADLY: both of Block Jam's anti-repetition
     * mechanisms are keyed on the level number, so freezing the level freezes
     * them. Beginner's endless phase produced **8 distinct shape-recipes across
     * 61 puzzles, 18 of them back-to-back repeats** — every board structurally
     * different, and every board made of the same pieces.
     *
     * `cycle` walks the effective level up and down inside its CEILING BAND
     * instead (Beginner 16–25, Intermediate 41–60, Expert 101–120). Difficulty
     * still stops rising — the band is the promise, and its target-score window
     * is only a few points wide — but the level number moves again, so the
     * existing machinery works unmodified. No new anti-repetition code.
     */
    hold: z.enum(['pin', 'cycle']).default('cycle'),
    /**
     * Module 73 (WP-73.4, user decision D2) — which ENDLESS-phase puzzles carry
     * a powered gate, set by the puzzle number instead of the effective level.
     *
     * Without it, powered gates follow the effective level (`poweredCountFor`),
     * and Beginner's `cycle` swings 25 -> 16 -> 25 across a line at 22: measured,
     * 23 of 56 endless puzzles powered, in runs of 7 on and 11 off. With it,
     * puzzle `k` of the endless phase is PLAIN when `k % plainEvery ===
     * plainOffset` and carries exactly one powered gate otherwise — "2 of every
     * 3" at `plainEvery: 3`, and never two plain puzzles in a row for any
     * `plainEvery >= 2`. The ramp is untouched. Absent (Intermediate, Expert,
     * whose every puzzle is already powered) = the effective-level rule.
     *
     * Module 74 (D1) — `poweredRun`: no endless puzzle in `[from, through]` is
     * plain. Without it the pattern put a plain puzzle on 31, the very next
     * puzzle after Surge unlocks on 30, and another on 34, so a player who had
     * just learned Surge had nothing to use it on. Bounded on both sides so the
     * puzzles BEFORE the unlock (28) keep their rhythm. The schema requires the
     * run to cover the Surge unlock and the `SURGE_POWERED_RUN - 1` puzzles after
     * it, so moving the unlock without moving this fails the load instead of
     * reopening the gap.
     */
    endlessPowered: z
        .object({
        plainEvery: z.number().int().min(2),
        plainOffset: z.number().int().min(0),
        poweredRun: z
            .object({ from: z.number().int().min(1), through: z.number().int().min(1) })
            .refine((r) => r.from <= r.through, { message: 'poweredRun: from must be <= through' })
            .optional(),
    })
        .refine((e) => e.plainOffset < e.plainEvery, {
        message: 'endlessPowered: plainOffset must be < plainEvery',
    })
        .optional(),
    /**
     * Module 77 (D4) — `'all'`: every configured power-up is unlocked from this
     * track's puzzle 1. Absent = the shared `unlocks.powerups` ordinal table.
     * Set on Expert only: a player normally arrives there through the other
     * two tracks, and Expert puzzle 1 (effective level 61, ~11 blocks, the
     * first L) was uncleared in play-test with no tools. Read ONLY through
     * `unlockPuzzleFor` (`powerups/powerup-registry.ts`).
     */
    powerupUnlocks: z.literal('all').optional(),
    /**
     * Module 77 (D6) — the puzzle whose CLEAR makes a player ready for the
     * next track: Beginner 30 (its ramp stops getting harder at 25, plus 5),
     * Intermediate 35. Absent = no next-track advice (Expert). Advice only —
     * no track is ever locked. Read through `model/track-readiness.ts`.
     */
    qualifyAt: z.number().int().positive().optional(),
})
    .refine((t) => t.lastLevel >= t.firstLevel, {
    message: 'track: lastLevel must be >= firstLevel',
});
export const generationConfigSchema = z
    .object({
    /**
     * 2 (WP-36.6): `density.blocksPerBand`, `colours.countPerBand` and
     * `arrows.constraintFractionPerBand` re-keyed from the four gameplay bands
     * onto the seven framework bands, and the arrows fraction became a ramped
     * `{min,max}` with a sibling `minArrowsPerBand` floor. A v1 file is rejected
     * rather than migrated: the loader's callers all fall back to the in-bundle
     * default (see `composition/blockjamConfig.ts`), which is a safer landing than
     * guessing how a four-band ramp should split across seven.
     *
     * 3 (Module 63, WP-63.D1): `unlocks.poweredGatesUnlockLevel` and the
     * `powered` section. A v2 file lands on the in-bundle default the same way.
     *
     * 4 (Module 65, WP-65.A1): the `tracks` array. A v3 file lands on the
     * in-bundle default like every earlier version — and here that fallback is
     * not merely safe but REQUIRED, because a config with no tracks cannot
     * describe a game that has no level ladder left to fall back to.
     */
    schemaVersion: z.literal(5),
    bands: z.array(bandSchema).min(1),
    tracks: z.array(trackSchema).length(TRACK_IDS.length),
    colours: coloursSchema,
    shapes: shapesSchema,
    unlocks: unlocksSchema,
    arrows: arrowsSchema,
    powered: poweredSchema,
    curve: curveSchema,
    difficultyScoreWeights: difficultyScoreWeightsSchema,
    density: densitySchema,
    recovery: recoverySchema,
    milestones: milestonesSchema,
    antiRepetition: antiRepetitionSchema,
    powerups: z.array(powerupSchema).min(1),
    rewards: rewardsSchema,
    moveBudget: moveBudgetSchema,
    /**
     * Module 144 (WP-144.1) — the confirm card for Wild Gates / Master Key.
     * `helpUses` is how many Use presses (per power-up) keep the card in its
     * longer "learning" form before it goes quiet. `.prefault` so a config
     * written before Module 144 parses (zod 4's `.default` returns unparsed).
     */
    powerupConfirm: z.object({ helpUses: z.number().int().positive().default(3) }).prefault({}),
})
    .superRefine((c, ctx) => {
    // Module 73 (WP-73.4) — the Surge unlock puzzle must never be a PLAIN endless
    // puzzle: Surge's lesson waits for a plate (`powerupOnBoard`), so a plain
    // unlock puzzle would hand the player a button that does nothing on the day
    // it arrives — the exact inversion this module exists to remove. Checked
    // here, against both numbers, so moving either one cannot reopen it quietly.
    const surgeAt = c.unlocks.powerups.surge;
    if (surgeAt === undefined)
        return;
    c.tracks.forEach((t, i) => {
        if (t.endlessPowered && isPlainEndlessPuzzle(t, surgeAt)) {
            ctx.addIssue({
                code: 'custom',
                path: ['tracks', i, 'endlessPowered'],
                message: `track ${t.id}: the Surge unlock puzzle (${surgeAt}) is a plain endless puzzle`,
            });
        }
        // Module 74 (D1) — the puzzles right after the unlock must be powered too.
        const run = t.endlessPowered?.poweredRun;
        const needThrough = surgeAt + SURGE_POWERED_RUN - 1;
        if (run && (run.from > surgeAt || run.through < needThrough)) {
            ctx.addIssue({
                code: 'custom',
                path: ['tracks', i, 'endlessPowered', 'poweredRun'],
                message: `track ${t.id}: poweredRun (${run.from}–${run.through}) must cover the Surge unlock ${surgeAt} through ${needThrough}`,
            });
        }
    });
});
/**
 * Module 74 (D1) — how many puzzles, starting at the Surge unlock, `poweredRun`
 * must keep powered (the unlock itself plus the next four). User decision: 31 and 34
 * powered after a puzzle-30 unlock.
 */
export const SURGE_POWERED_RUN = 5;
/**
 * Module 73 (WP-73.4) — is endless puzzle `ordinal` on this track one of the
 * PLAIN ones (no powered gate)? `false` for every ramp puzzle and for a track
 * with no `endlessPowered`, where the effective-level rule still decides.
 * Structural parameter type so the schema's own refinement can call it.
 */
export function isPlainEndlessPuzzle(track, ordinal) {
    const e = track.endlessPowered;
    if (!e || ordinal <= track.rampLength)
        return false;
    if (e.poweredRun && ordinal >= e.poweredRun.from && ordinal <= e.poweredRun.through)
        return false;
    return ordinal % e.plainEvery === e.plainOffset;
}
// ─── Loader ───────────────────────────────────────────────────────────────────
/**
 * Parse + validate an untyped config object (e.g. the JSON file the Studio wrote,
 * or a fixture). Throws a `ZodError` with a readable path on invalid input.
 */
export function loadGenerationConfig(raw) {
    return generationConfigSchema.parse(raw);
}
// ─── Default (parity with the pre-WP-35.1 generator constants) ─────────────────
/**
 * Kept byte-for-byte in sync with `games/blockjam/config/generation.json` — the
 * drift test enforces `loadGenerationConfig(json)` deep-equals this value.
 */
/** A power-up's default entry, priced from the registry (Module 129). */
function registryPowerup(id) {
    const p = ECONOMY.powerups[id];
    return {
        id,
        cost: { coins: p.coins, gems: 0 },
        perLevelCap: p.perPuzzleCap,
        freeUses: p.freeUses,
    };
}
export const DEFAULT_GENERATION_CONFIG = loadGenerationConfig({
    schemaVersion: 5,
    // Module 65 (WP-65.A1). `rampLength === lastLevel - firstLevel + 1` in all
    // three, so f(k) = firstLevel + k - 1 exactly and every level of the slice is
    // visited once before the hold. Beginner's puzzle 1 is therefore level 1 at
    // variant 0 — byte-identical to the numbered game's first board.
    tracks: [
        // Module 73 (D2) — 2 of every 3 endless puzzles powered, so the Surge
        // unlock (30) is powered. Module 74 (D1) — none plain from 30 to 34, so
        // 31 and 34 flip to powered: after 28 the next plain puzzle is 37. The
        // schema checks both pairings.
        {
            id: 'beginner',
            firstLevel: 1,
            lastLevel: 25,
            rampLength: 25,
            hold: 'cycle',
            qualifyAt: 30,
            endlessPowered: { plainEvery: 3, plainOffset: 1, poweredRun: { from: 30, through: 34 } },
        },
        {
            id: 'intermediate',
            firstLevel: 26,
            lastLevel: 60,
            rampLength: 35,
            hold: 'cycle',
            qualifyAt: 35,
        },
        // Module 77 (D4) — every power-up from puzzle 1 (see `powerupUnlocks`).
        {
            id: 'expert',
            firstLevel: 61,
            lastLevel: 120,
            rampLength: 60,
            hold: 'cycle',
            powerupUnlocks: 'all',
        },
    ],
    bands: [
        {
            name: 'Easy',
            gridSize: 5,
            minSolveMoves: 3,
            maxAttempts: 80,
            freeSolverLimit: 40000,
        },
        {
            name: 'Medium',
            gridSize: 6,
            minSolveMoves: 4,
            maxAttempts: 150,
            freeSolverLimit: 8000,
        },
        {
            name: 'Hard',
            gridSize: 7,
            minSolveMoves: 5,
            maxAttempts: 200,
            freeSolverLimit: 4000,
        },
        {
            name: 'Expert',
            gridSize: 8,
            minSolveMoves: 6,
            maxAttempts: 250,
            freeSolverLimit: 3000,
        },
    ],
    colours: {
        // Cyan is a full 7th palette entry as of WP-35.15 (locked decision 10). It was
        // already in the renderer's colour map and in `block-visuals.json`; only the
        // GENERATION palette was still six long — which also left the legacy
        // generator indexing `palette[6]` as `undefined` on Expert boards.
        palette: ['Red', 'Blue', 'Green', 'Yellow', 'Purple', 'Orange', 'Cyan'],
        // WP-36.7. Measured before the retune, the first six levels all shipped TWO
        // colours and levels 4–25 shipped four; the count is a headline part of what
        // makes a board look different from the one before it, so it climbs faster
        // early now (2 → 4 across Beginner) and then flattens where the real
        // difficulty comes from density instead.
        //
        // The count is clamped to the BLOCK count in `drawRecipe` — a colour with no
        // block would leave a gate nothing can exit through — so these are ambitions
        // that a recovery level's smaller board quietly trims.
        countPerBand: {
            Beginner: { min: 2, max: 4 },
            Easy: { min: 4, max: 5 },
            'Easy+': { min: 5, max: 5 },
            Medium: { min: 5, max: 6 },
            'Medium+': { min: 6, max: 6 },
            Advanced: { min: 6, max: 6 },
            Infinite: { min: 6, max: 7 },
        },
    },
    shapes: {
        maxSameFamily: 3,
        families: [
            // WP-35.14: a family's weight must be NON-ZERO in every band from the one
            // containing its unlock level onward, or `unlocks.shapes` is decorative.
            // Measured before this WP: 1x4 unlocked at 25 but first appeared on 71, and
            // the L unlocked at 60 but first appeared on 151, because the band covering
            // their unlock level weighted them 0. `shapeSchedule()` + a test now hold
            // configured == effective. Weights control HOW OFTEN, never WHETHER.
            // The ramp itself: 1x2 dominates early and recedes; the bigger and more
            // awkward a family is, the later it peaks.
            { id: '1x2', weightPerBand: { Easy: 100, Medium: 55, Hard: 38, Expert: 26 } },
            { id: '1x3', weightPerBand: { Easy: 30, Medium: 30, Hard: 25, Expert: 20 } },
            { id: '2x2', weightPerBand: { Easy: 15, Medium: 20, Hard: 18, Expert: 16 } },
            { id: '1x4', weightPerBand: { Easy: 10, Medium: 15, Hard: 14, Expert: 14 } },
            // 2x3 and L unlock inside gameplay-Medium (levels 40 and 60), so their Easy
            // weight is never consulted — they are still locked there.
            { id: '2x3', weightPerBand: { Easy: 0, Medium: 8, Hard: 10, Expert: 12 } },
            { id: 'L', weightPerBand: { Easy: 0, Medium: 6, Hard: 8, Expert: 10 } },
        ],
    },
    unlocks: {
        arrowsUnlockLevel: 5,
        shapes: { '1x3': 8, '2x2': 16, '1x4': 26, '2x3': 41, L: 61 },
        // Module 50 (WP-50.26) — re-ordered with the prices below: cheapest first,
        // and unlock order now follows price order rather than crossing it.
        // Module 65 (WP-65.F1, schema 5) — these are PUZZLE ORDINALS, the same on
        // every track, not effective levels. Measured against each track's slice of
        // effective levels the old numbers (20/22/35/50) gave Beginner two power-ups
        // it could never reach and Expert nothing to earn. Price order still climbs
        // with unlock order (25 < 40 < 50 < 100). `poweredGatesUnlockLevel` below
        // stays an effective level: it decides board CONTENT, and Surge's lesson
        // waits for the first board that actually has a plate (`powerupOnBoard`).
        // Module 67 (WP-67.B4) — Gate Widener at 50, continuing the every-10
        // cadence (user decision 2026-09-05). Its 40-coin price matches Surge, so
        // the price-climbs-with-unlock note above gains its one deliberate
        // exception: the last unlock is a Surge-priced tool, not a premium one.
        // Module 73 (WP-73.3, user decision D1) — Hammer and Surge swap: Hammer 20,
        // Surge 30. At 20 Surge was handed over two puzzles BEFORE Beginner's first
        // powered gate (22), so its lesson and free uses met a board it could not
        // act on. At 30 it comes after powered gates on every track (Intermediate
        // and Expert have them from puzzle 1). Hammer acts on any board, so it is
        // never idle at 20. Price order no longer climbs with unlock order
        // (25, 50, 40, 100, 40); free uses (D3) make the first contact free anyway.
        powerups: { rainbowGates: 10, hammer: 20, surge: 30, colorDelete: 40, gateWidener: 50 },
        // Module 63 — decision 2: L1–24 byte-identical, powered gates from 25.
        poweredGatesUnlockLevel: 22,
    },
    arrows: {
        // WP-35.14: Easy was 0, so Arrows "unlocked at 5" but first appeared on level
        // 26. It must be high enough that `blocks × fraction` reaches 1 on an early
        // 4-block board — 0.25 is the smallest value that does.
        //
        // WP-36.8, the moderate curve: ~30% of blocks carry an arrow by level 40,
        // reaching 2 by level 25 (it was level 75) and 3 by level 100. The measured
        // problem was not the fraction but its SHAPE — one flat number per gameplay
        // band, floored, held the count at exactly 1 from level 5 to level 70. The
        // count is still `fraction × blocks`, never a random addition; it just ramps
        // now, rounds instead of flooring, and has a floor under it.
        constraintFractionPerBand: {
            Beginner: { min: 0.15, max: 0.22 },
            Easy: { min: 0.22, max: 0.3 },
            'Easy+': { min: 0.3, max: 0.34 },
            Medium: { min: 0.34, max: 0.36 },
            'Medium+': { min: 0.36, max: 0.38 },
            Advanced: { min: 0.38, max: 0.4 },
            Infinite: { min: 0.4, max: 0.4 },
        },
        // The floor is what makes the curve hold on a recovery level, whose smaller
        // board would otherwise take the mechanic away again (a dip is meant to be a
        // breather, not a lesson un-taught).
        minArrowsPerBand: {
            Beginner: 0,
            Easy: 1,
            'Easy+': 2,
            Medium: 2,
            'Medium+': 2,
            Advanced: 3,
            Infinite: 3,
        },
    },
    powered: {
        // Module 63 (WP-63.D1). Nothing before the unlock — the ramp is 0 through
        // Easy+ and the unlock level itself forces one (§3 rule 1) — then one
        // powered gate through Medium, stepping to two across Medium+ and holding
        // two from Advanced on. Tuned live in the Level Studio; measured in D5.
        countPerBand: {
            Beginner: { min: 0, max: 0 },
            Easy: { min: 0, max: 0 },
            'Easy+': { min: 1, max: 1 },
            Medium: { min: 1, max: 1 },
            'Medium+': { min: 1, max: 2 },
            Advanced: { min: 2, max: 2 },
            Infinite: { min: 2, max: 2 },
        },
        keepUnpoweredGates: 1,
        // Cells from the gate's wall: Easy/Medium ≤ 2, Hard ≤ 4, Expert unrestricted.
        traceLengthCapPerBand: { Easy: 2, Medium: 2, Hard: 4, Expert: null },
    },
    curve: {
        // Level ranges are the Difficulty Framework §5 example. The score windows are
        // RE-FITTED in WP-36.9 to the curve the WP-36.7/.8 retune actually produces,
        // measured with the exact score over levels 1–200 (the windows before it were
        // fitted to the WP-35.9 curve, which the retune left ~10 points below the
        // boards it was now generating — 16 of 200 levels fell outside tolerance).
        //
        // Fitted from the 5-level moving median of PLAIN levels, so a recovery dip or
        // a milestone peak cannot drag a window towards itself:
        //   L1–5 ≈ 30 · L6–12 ≈ 36 · L13–25 ≈ 36→43 · L26–45 ≈ 40→42
        //   L46–70 ≈ 42→45 · L71–100 ≈ 46→47 · L101–150 ≈ 47→48 · L151+ ≈ 50
        //
        // The curve flattens sharply past ~L70 — partly real (occupancy and move
        // count plateau once the grid stops growing) and partly an artefact: the
        // exact score hits its node budget on 47 of 50 Advanced levels and 50 of 50
        // Infinite ones, so those are LOWER bounds. Widening the windows late would
        // be fitting to a measurement limit; they are kept narrow instead.
        bands: [
            {
                name: 'Beginner',
                levelRange: { min: 1, max: 7 },
                // WP-50.5 handover 2: the floor was 29, and the band's own density rules
                // cannot reach it — 3 blocks score 19.5 and 29 first becomes reachable at
                // 5. Level 1 is capped at `startBlocks` (3) by locked decision 12, so the
                // very first level missed its target by 9.5 against a tolerance of 8, and
                // so did the level-7 recovery dip. Lowering the floor to what 3–4 blocks
                // actually score fixes both, and steepens the early ramp while it is at it.
                targetScore: { min: 20, max: 35 },
                difficulty: 'Easy',
            },
            {
                // WP-36.7, user decision 2026-07-31: framework-Easy maps to gameplay
                // MEDIUM, i.e. the 6×6 grid starts at level 11 instead of level 26.
                //
                // Measured over levels 11–25, every board solver-verified: the retuned
                // ramp asks for 6→7 blocks, and a 5×5 (25 cells) can only hold 7 at 68%
                // occupancy — against the 47–55% the rest of the ladder runs at, with 6×
                // more candidates rejected as unsolvable. The same 7 blocks on a 6×6 sit
                // at 47%. Raising `maxCellFraction` was the smaller change and was tried
                // first; it buys 6 blocks at 59% and no more.
                //
                // What this costs: levels 11–25 report `Medium` to saves and analytics.
                // Coin payout is unaffected — the tier comes from the difficulty SCORE,
                // not the band — and the score curve for these levels is unchanged, so
                // the band label moves without the difficulty moving.
                name: 'Easy',
                levelRange: { min: 8, max: 15 },
                targetScore: { min: 35, max: 41 },
                difficulty: 'Medium',
            },
            {
                name: 'Easy+',
                levelRange: { min: 16, max: 25 },
                targetScore: { min: 41, max: 44 },
                difficulty: 'Medium',
            },
            {
                name: 'Medium',
                levelRange: { min: 26, max: 40 },
                targetScore: { min: 44, max: 47 },
                difficulty: 'Medium',
            },
            {
                name: 'Medium+',
                levelRange: { min: 41, max: 60 },
                targetScore: { min: 47, max: 50 },
                difficulty: 'Hard',
            },
            {
                name: 'Advanced',
                levelRange: { min: 61, max: 100 },
                targetScore: { min: 50, max: 53 },
                difficulty: 'Hard',
            },
            {
                name: 'Infinite',
                levelRange: { min: 101, max: null },
                targetScore: { min: 53, max: 58 },
                difficulty: 'Expert',
            },
        ],
        tolerance: 8,
        candidates: 5,
        candidateAttemptBudget: 18,
        milestoneLift: 0.9,
    },
    difficultyScoreWeights: {
        moves: 1,
        branchingFactor: 1,
        decisionPoints: 1,
        occupancy: 1,
        shapeVariety: 1,
        colourCount: 1,
        searchDepth: 1,
    },
    density: {
        startBlocks: 3,
        startDependencies: 1,
        recoveryBlockDrop: 2,
        // WP-36.7 lifted all four. This is the ceiling that actually decides how big
        // a board gets — `drawRecipe` stops adding blocks when the next one would
        // exceed it, so a ramp asking for more than the grid can hold shows up as a
        // silently short board rather than as an error. Sweeping it was how the
        // 5×5-vs-6×6 decision below was measured.
        // At these values, L1–150: occupancy 42–57% (was 40–55%), zero fallbacks, no
        // level over the 300ms generation budget.
        maxCellFraction: { Easy: 0.6, Medium: 0.68, Hard: 0.7, Expert: 0.7 },
        // Ramps measured in WP-35.11 and RE-BALANCED in WP-35.14: once the shape
        // schedule started delivering 1x4 / 2x3 / L on time, the same block count
        // carried far more CELLS — occupancy jumped and generation peaked at 552ms
        // against a 300ms budget. Block count is only a proxy for fullness when every
        // block is a 1x2; these numbers restore ~40–47% occupancy per band.
        //
        // WP-36.7 retune, on the framework bands WP-36.6 re-keyed onto. Measured
        // before it, the ramp gave FOUR blocks for every level from 2 to 12 and five
        // from 13 to 25 — so a player's second board and their twenty-fifth were the
        // same size. Beginner now climbs 4 → 6 and Easy 6 → 7, which is the whole
        // "gentle but varied" ask; level 1 is still capped at `startBlocks`.
        //
        // The later bands are set to what their GRID can actually deliver, measured
        // rather than guessed. A first cut ramped them to 11–13 and 48 of the 50
        // Advanced levels came back short — an ambition that never lands is not a
        // knob, it is a fiction, and it hides the real constraint (`maxCellFraction`
        // and the grid size) from whoever next opens the Studio.
        //
        // Block count is deliberately FLAT from 26 to 70: those levels are all 6×6,
        // and a 6×6 at 56% holds about seven blocks whatever the ramp asks for.
        // Difficulty across that stretch comes from the shape schedule (2x3 at 40,
        // the L at 60), the colour count and the arrow curve instead — all of which
        // the difficulty score measures.
        // WP-50.24: the block ramp has to move WITH the grid, not after it. The
        // compression brings 7×7 forward from level 71 to 41 and 8×8 from 151 to
        // 101, and WP-50.5 measured that a bigger grid ALONE makes boards easier —
        // seven blocks on an 8×8 is a sparse board. Raised here so the recipe fills
        // the new grid rather than rattling around in it. `maxCellFraction` had to
        // move too: the recipe is truncated at the CELL budget, so raising this
        // alone left the generator shipping seven blocks when it had asked for nine.
        blocksPerBand: {
            Beginner: { min: 4, max: 6 },
            Easy: { min: 6, max: 7 },
            'Easy+': { min: 7, max: 9 },
            Medium: { min: 9, max: 10 },
            'Medium+': { min: 10, max: 11 },
            Advanced: { min: 11, max: 12 },
            Infinite: { min: 12, max: 14 },
        },
    },
    recovery: {
        everyNLevels: 7,
        dropBands: 1,
    },
    milestones: {
        levels: [10, 25, 50, 75, 100],
    },
    antiRepetition: {
        noRepeatWindow: 5,
        maxConsecutiveSameShape: 2,
        maxConsecutiveSameColourSet: 2,
    },
    // Module 50 (WP-50.26) — re-priced against a wallet that is ~20× smaller.
    //
    // WP-43.10's prices (50 / 200 / 450) were ranked 1 : 3 : 6 and sized against
    // the 50-coin rewarded top-up that WP-50.29 has now retired. Both of their
    // anchors are gone: a level paid 80–220 coins and now pays 5–40, and it pays
    // ONCE (WP-50.25). Measured on the retuned 50-level scan, lifetime income is
    // 170 coins by level 20, 340 by 35, 600 by 50 — so these three cost 2.5, 5.0
    // and 5.0 levels of income at their unlock level, against 0.6 of one level
    // before. The sticker price of a Hammer did not move; its real price went up
    // about 16×. See planning/50-prelaunch-fixes/findings/WP-50.26-prices.md.
    //
    // The ARRAY ORDER is the bar's render order (`PowerupBar` walks it as-is), so
    // it is sorted cheapest-first here rather than in a component — a balance
    // decision expressed as config, per BlockJam_Generation_Rules §2. Unlock order
    // now matches price order; before this the cheapest power-up unlocked last.
    //
    // Each amount is ALSO a literal in the Supabase `economy_apply` debit
    // allowlist (migration 0013) — changing one here without the other fails in a
    // player's hands, not in CI.
    // Module 73 (WP-73.6, decision D3) — every power-up gets TWO free uses, per
    // track, once ever, so a player learns what each one does before paying.
    // Surge and Widener come down from three so there is one rule. Free uses
    // never reach `economy_apply` (migration 0013): the client debits nothing and
    // records them in the save, so no allowlist change is needed.
    // Module 74 (D2) — every power-up is once per puzzle, Rainbow's cap. A single
    // use makes each one a deliberate purchase; the Hammer at 3 was the strongest
    // item AND the one usable most often. Client-side only: `economy_apply`
    // allowlists (kind, id, currency, amount), never a per-level count.
    // Module 97 — every price ×10 with the rest of the coin economy (same
    // ratios against the ×10 reward curve). Older comments below quote old prices.
    // Module 129 — prices, free uses and caps come from the price registry.
    powerups: [
        registryPowerup('rainbowGates'),
        // Module 73 (WP-73.3) — Hammer before Surge: the array is the power-up
        // bar's left-to-right order, and Module 50 made that order the UNLOCK
        // order so a new player's first unlocked button is never to the right of a
        // locked one. The swap (Hammer 20, Surge 30) moves the two buttons with it.
        registryPowerup('hammer'),
        // Module 63 (WP-63.E5) — Surge: 40 coins, free uses once
        // ever (§4 divergence 4 — the grant is a persisted count, never a
        // per-load counter).
        registryPowerup('surge'),
        registryPowerup('colorDelete'),
        // Module 67 (WP-67.B4) — Gate Widener: Surge's exact economy shape (40
        // coins, once-ever free uses; user decision 2026-09-05). Allowlisted by
        // migration 0015.
        // Module 92 — RETIRED (user, 2026-10-05): gates are width-matched by the
        // generator, so it almost never helped. Switched off, not deleted.
        { ...registryPowerup('gateWidener'), retired: true },
    ],
    rewards: {
        scaleWithScore: true,
        // Calibrated to the WP-35.11/.15 measured curve (L1 ≈ 25 → L150 ≈ 47, the
        // Infinite band approaching ~53). The thresholds lined up with the band score
        // windows, so the payout distribution matched the old band-based rule and
        // only differed where a board landed off its rung's usual score — which is
        // exactly the case this exists to reward.
        //
        // ⚠ **DELIBERATELY NOT RETUNED in WP-36.9** (user decision 2026-07-31), even
        // though the WP-36.7/.8 retune moved the curve out from under them. Measured
        // over L1–200 with the exact score, under these unchanged thresholds:
        //
        //   tier    coins   before   after   first level reaching it
        //   Easy      80      26        6
        //   Medium   160      37       24    L13 → L5
        //   Hard     300      89       99    L36 → L15
        //   Expert   500      48       71    L89 → L62
        //   total coins for clearing L1–200: 58,700 → 69,520  (+18%)
        //
        // So the same 200 levels now pay 18% more, and a player reaches the top tier
        // 27 levels earlier. That is a real economy change, reported rather than
        // absorbed: raising a threshold here is a live-economy decision, and the four
        // AMOUNTS are pinned by the server allowlist regardless (see reward-tier.ts).
        scoreTiers: [
            { tier: 'Easy', minScore: 0 },
            { tier: 'Medium', minScore: 31 },
            { tier: 'Hard', minScore: 42 },
            { tier: 'Expert', minScore: 50 },
        ],
    },
    moveBudget: {
        // Retuned by WP-50.28c on ABSOLUTE slack. See `moveBudgetSchema`.
        //
        // ⚠️ TEMPORARY TEST VALUE (Module 51, WP-51.5, 2026-08-13) — all four bands
        // are at 1.1 while the product owner measures how tight the wall feels; they
        // cleared ~150 levels well inside the WP-50.28c budgets (Easy 1.5 / Medium
        // 1.35 / Hard 1.3 / Expert 1.3). **TO REVERT: put those four numbers back
        // HERE and in the identical block in `games/blockjam/config/generation.json`
        // — the two are pinned to each other by the drift guard in
        // `generation-config.test.ts`, so neither can be changed alone.**
        //
        // Only newly GENERATED boards are affected: `moveBudgetFor` runs once, at
        // generation, and the result is persisted on the board (and on the
        // pre-generated level N+1), so a level already in progress keeps the budget
        // it was built with until it is regenerated.
        multiplierPerBand: { Easy: 1.1, Medium: 1.1, Hard: 1.1, Expert: 1.1 },
        // Module 83 (WP-83.2) — Expert 1.1 → 1.2, user decision 2026-10-02 ("increase
        // the margin to some extent in Expert only"): with `proxyPar` under-reading
        // par by up to 2.5× on 11-block boards, 1.1 had put 19 of Expert's first 40
        // budgets below the shortest known solution. Intermediate stays at 1.2 and
        // gains only the `solution` floor below.
        // Settled at 1.15 after the user cleared Expert 7 inside the OLD 20 (the
        // route the solver stores is straight full slides; a drag can stop part-way
        // and turn, so the solver overstates par — Daily 2026-10-02: route 16, drag
        // par 10, cleared in 12).
        // Module 146 (user, 2026-10-23) — Beginner 1.4 → 1.05, Intermediate 1.2 → 1.1:
        // the user finished Beginner 53–65 with ~9 moves left and Intermediate 48–74
        // with ~6, never fewer than 4. Measured over puzzles 1–100: spare over the
        // route 4.5 → 0.9 (Beginner) and 1.8 → 0.7 (Intermediate); the route floor
        // below still keeps every board winnable. Expert unchanged until the
        // drag-count "Solvable in" POC. Evidence: planning/146-move-budget-tighten/.
        multiplierPerTrack: { beginner: 1.05, intermediate: 1.1, expert: 1.15 },
        // Module 83 — the stored-solution floor is EXACTLY the route's length (user
        // decision 2026-10-02 after the Daily was raised 15 → 18 for a 10-drag
        // puzzle): a straight-slide route is already a loose upper bound on what a
        // drag needs, so no margin is stacked on it. See `moveBudgetSchema.solution`.
        solution: { maxNodes: 60000, extra: 0, multiplier: 1 },
        // Module 83 — the Reveal's price; user decision 2026-10-02. ×10 by Module 97.
        reveal: { ...ECONOMY.sinks.reveal },
        // Module 69 — chosen by the user from four modelled candidates. Delivered
        // over Expert's opening: 30 / 26 / 23 / 26 / 24 / 25 / 20 / 18, against
        // 23 / 20 / 18 / 21 / 20 / 21 / 18 / 18 before it. Intermediate's first
        // puzzles gain 2–3 and Beginner's 0–1; both decay to unchanged by puzzle 9.
        // Module 146 — Intermediate peaks at 1.2, Expert keeps 1.45. Beginner 1.2 → 1.1
        // after the user's web play-test (2026-10-24): its first puzzles still felt loose.
        entryGrace: {
            puzzles: 8,
            multiplier: 1.45,
            multiplierPerTrack: { beginner: 1.1, intermediate: 1.2 },
        },
        // Module 146 — the Daily keeps the 1.2 it had on Intermediate's multiplier.
        dailyMultiplier: 1.2,
        parEstimate: { perBlock: 1, perDependency: 0.7 },
        witnessCap: 1.05,
        fallback: { floor: 20, blocksMultiplier: 4 },
        // Module 118 (user, 2026-10-14) — +3 then +3 (was [2, 1]); coins buy +5.
        adGrants: [...ECONOMY.caps.adMoveGrants],
        maxAdsPerLevel: ECONOMY.caps.maxAdsPerLevel,
        warnAtRemaining: 3,
    },
    powerupConfirm: { helpUses: 3 },
});
// ─── Derived helpers (consumed by the generator) ───────────────────────────────
/**
 * The band config for a given difficulty name.
 *
 * **This is the only way in.** The gameplay band itself comes from the curve —
 * `targetScoreForLevel(config, n).difficulty` — never from a level number looked
 * up here. WP-50.23 deleted the `difficultyForLevel` helper that did the latter;
 * it read the legacy `bands[].levelRange`, disagreed with the curve, and had had
 * no caller since WP-35.23.
 */
export function bandConfigFor(config, name) {
    const band = config.bands.find((b) => b.name === name);
    if (!band)
        throw new Error(`No band config for difficulty "${name}"`);
    return band;
}
